import { useTranslation } from 'next-i18next';
import { getCountryNameFromCountryISOCode } from '@helpers/utils/general';
import { Product } from '@interfaces/models/product';
import { Country } from '@interfaces/models/country';

export interface DisplayElementOptions {
  productSize: boolean;
  productLocation: boolean;
}

export interface AriaLabelBuilderParams {
  product: Product;
  shouldDisplaySize: boolean;
  shouldShowProductTags: boolean;
  isNewProduct: boolean;
  tags: string[];
  getTagName: (tag: string) => string;
  displayElementOptions: DisplayElementOptions;
  countryList: Country[];
  tA11y: ReturnType<typeof useTranslation>['t'];
}

export class AriaLabelBuilder {
  private readonly product: Product;
  private readonly shouldDisplaySize: boolean;
  private readonly shouldShowProductTags: boolean;
  private readonly isNewProduct: boolean;
  private readonly tags: string[];
  private readonly getTagName: (tag: string) => string;
  private readonly displayElementOptions: DisplayElementOptions;
  private readonly countryList: Country[];
  private readonly tA11y: ReturnType<typeof useTranslation>['t'];

  constructor(params: AriaLabelBuilderParams) {
    this.product = params.product;
    this.shouldDisplaySize = params.shouldDisplaySize;
    this.shouldShowProductTags = params.shouldShowProductTags;
    this.isNewProduct = params.isNewProduct;
    this.tags = params.tags;
    this.getTagName = params.getTagName;
    this.displayElementOptions = params.displayElementOptions;
    this.countryList = params.countryList;
    this.tA11y = params.tA11y;
  }

  buildSoldStatus(): string {
    return this.product.sold ? this.tA11y('A11Y.PRODUCT_TILE.SOLD_ITEM') : '';
  }

  buildProductName(): string[] {
    const parts = [];
    if (this.product?.brand.localizedName) {
      parts.push(this.product.brand.localizedName);
    }
    parts.push(this.product.name);
    return parts;
  }

  buildProductTags(): string[] {
    if (this.shouldShowProductTags) {
      return this.tags.map((tag) => this.tA11y('A11Y.PRODUCT_TILE.TAG', { productTileTag: this.getTagName(tag) }));
    } else if (this.isNewProduct) {
      return [
        this.tA11y('A11Y.PRODUCT_TILE.TAG', {
          productTileTag: this.tA11y('common:ALERTS.LANDING.NEW_LABEL'),
        }),
      ];
    }
    return [];
  }

  buildProductSize(): string {
    return this.shouldDisplaySize && this.product?.size?.size
      ? `${this.tA11y('A11Y.PRODUCT_TILE.SIZE')}: ${this.product.size.size}`
      : '';
  }

  buildProductPrice(): string[] {
    if (this.product?.regularPrice?.formatted) {
      return [
        `${this.tA11y('A11Y.PRODUCT_TILE.ORIGINAL_PRICE')}: ${this.product?.regularPrice?.formatted}`,
        `${this.tA11y('A11Y.PRODUCT_TILE.CURRENT_PRICE')}: ${this.product?.price?.formatted}`,
      ];
    } else {
      return [`${this.tA11y('A11Y.PRODUCT_TILE.PRICE')}: ${this.product?.price.formatted}`];
    }
  }

  buildProductDuties(): string {
    return this.product.hasNewTaxes ? this.tA11y('common:CATALOG.DUTIES.PRODUCT_CARD.TAG') : '';
  }

  buildProductLocation(): string {
    return this.displayElementOptions.productLocation &&
      getCountryNameFromCountryISOCode(this.countryList, this.product.isoCountry)
      ? `${this.tA11y('A11Y.ICON.LOCATION')} ${getCountryNameFromCountryISOCode(
          this.countryList,
          this.product.isoCountry,
        )}`
      : '';
  }

  generateAriaLabel(): string {
    return [
      this.buildSoldStatus(),
      ...this.buildProductName(),
      ...this.buildProductTags(),
      this.buildProductSize(),
      ...this.buildProductPrice(),
      this.buildProductDuties(),
      this.buildProductLocation(),
    ]
      .filter(Boolean)
      .join(', ');
  }
}
